import * as React from "react";
import { GlobalHotKeys } from "react-hotkeys";
import { connect } from "react-redux";

import * as Actions from "./actions";

import { ServerError } from "../../errors/serverError/ServerError";
import { OverreadFixedComment, SessionNote, Permission, CoughAnalysisSetsFixedCommentsDisplay, CoughAnalysisSetComment } from "../../misc/api/apiTypes";
import { ItemMetadata } from "../../misc/components/analysis/ItemMetadata";
import { CommentsComparisonDialog } from "../../misc/components/CommentsComparisonDialog";
import { NotesModal } from "../../misc/components/NotesModal";
import AcceptabilityDashboard from "./components/AcceptabilityDashboard";
import AnalysisSetDisplayDialog from "./components/AnalysisSetDisplayDialog";
import EventList from "./components/EventList";
import HourSegmentsModal from "./components/HourSegments/HourSegmentsModal";
import { LoadingAnalyse } from "./components/LoadingAnalyse";
import ResumePlayback from "./components/ResumePlayback";
import WaveformContainer from "./components/WaveformContainer";
import { FilteredNotesNotificationBanner } from "./components/FilteredNotesNotificationBanner";
import {
    AnalyseState,
    EditModeEnum,
    LoadingStateEnum,
    PlayStateEnum,
    SessionMetadata
} from "./interfaces/state";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { ErrorBoundary } from "../../errors/ErrorBoundary";
import { useWebPortalLegacyClient, useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";
import { AnalysisMode } from "./store";
import { useCoughMarshalApiClient } from "../../misc/api/CoughMarshalApiClient";
import { Positions } from "./MappingPositionHelper";
import VisualInspectionDashboard from "./components/VisualInspectionDashboard";
import { InitializeCommentBlindingAsyncAction, InitializeAnalysisSetCommentAsyncAction, InitializeCurrentAnalysisSetAsyncAction, InitializeVisualReviewForAnalysisSetAction } from "./actions";
import { useAuth } from "react-oidc-context";
import { useSecurityContext } from "../../Core/SecurityContext";
import { CoughAnalysisSet } from "webportalclient";

interface AnalyseAppProps {
    studyId: string;
    isViewOnly: boolean;
    sessionMetadata: SessionMetadata;
    isPlaying: boolean;
    mode: AnalysisMode;
    isEditMode: EditModeEnum;
    primaryAnalystSetId: string;
    loadingState: LoadingStateEnum;
    isLoadError: boolean;
    configErrorMessage: string;
    isFixedCommentsDialogOpen: boolean;
    availableFixedComments: OverreadFixedComment[];
    zoomLevel: number;
    selectedWaveform: string;
    analysisSetsLoaded: boolean;
    analysisSets: CoughAnalysisSetsFixedCommentsDisplay[];
    isSessionNotesVisible: boolean;
    isAddingNote: boolean;
    sessionNotes: SessionNote[];
    playbackStatus: string;
    positions: Positions;
    comments: CoughAnalysisSetComment[];
    coughAnalysisSet: CoughAnalysisSet;
    dispatch: any;
    blindComments: boolean;
}

export const OnIgnoreEnter = (e) => {
    if (e !== undefined && e.keyCode === 13) { // Handled by hot keys
        e.preventDefault();
    }
}

const GetAnalysisSet = (analysisSetId: string, analysisSets: CoughAnalysisSetsFixedCommentsDisplay[]) => analysisSets ? analysisSets.find(anSet => anSet.Id === analysisSetId) : null;

export const AnalyseApp = (props: AnalyseAppProps) => {
    const legacyClient = useWebPortalLegacyClient();
    const client = useWebPortalApiClient();
    const coughMarshalClient = useCoughMarshalApiClient();
    const user = useAuth();
    const { currentUserPermissions } = useSecurityContext();

    const [currentCoughAnalysisSetVisualReviewStatus, setCurrentCoughAnalysisSetVisualReviewStatus] = React.useState(false);
    React.useEffect(() => {
        props.dispatch(
            Actions.InitializeSessionMetadataAsyncAction.create(
                legacyClient,
                client,
                props.primaryAnalystSetId,
                coughMarshalClient
            ))
        props.dispatch(
            Actions.InitializeCurrentAnalysisSetAsyncAction.create(
                client,
                props.primaryAnalystSetId
            ))
        props.dispatch(
            Actions.InitializeVisualReviewForAnalysisSetAction.create(
                client,
                legacyClient,
                props.primaryAnalystSetId,
            ))
        props.dispatch(
            Actions.InitializeAnalysisSetCommentAsyncAction.create(
                client,
                props.primaryAnalystSetId
            ))
        props.dispatch(
            Actions.InitializeCommentBlindingAsyncAction.create(
                client,
                user.user.profile.email,
                props.primaryAnalystSetId,
                currentUserPermissions
            )
        );
    }, [props.primaryAnalystSetId]);

    const renderLoadingBar = () => {
        if (props.loadingState !== LoadingStateEnum.Loaded) {
            return <LoadingAnalyse summary={props.loadingState} />;
        } else {
            return null;
        }
    };

    const renderViewModeInfo = () => {
        if (props.loadingState === LoadingStateEnum.Loaded && props.isViewOnly) {
            return (
                <Alert show={true} category={AlertCategoryEnum.Info}>
                    Recording in view only mode, no data edits can be made
                </Alert>
            );
        }
    }

    const renderErrorsBar = () => {
        return (
            <Alert show={props.configErrorMessage !== null} category={AlertCategoryEnum.Danger}>
                {props.configErrorMessage}
            </Alert>
        );
    };

    const renderLinkedWarning = () => {
        return (
            <Alert show={props.analysisSetsLoaded && GetAnalysisSet(props.primaryAnalystSetId, props.analysisSets).IsSourceOfAnother} category={AlertCategoryEnum.Warning}>
                <b>WARNING:</b> This analysis set has linked analysis sets. Changing this analysis set will change any linked analysis sets.
            </Alert>
        );
    };

    const renderWaveformContainer = () => {
        if (props.sessionMetadata && props.selectedWaveform && props.positions) {
            return <WaveformContainer />;
        } else {
            return null;
        }
    };

    const renderAnalysisSetDialog = () => {
        if (props.sessionMetadata) {
            return <AnalysisSetDisplayDialog />;
        } else {
            return null;
        }
    };

    const fetchReviewData = async () => {
        try {

            const currentCoughAnalysisSet = await client.client.getCoughAnalysisSet(props.primaryAnalystSetId);
            setCurrentCoughAnalysisSetVisualReviewStatus(currentCoughAnalysisSet.visualReview);
        } catch (error) {
            console.error("Error fetching Current Cough Analysis Set:", error);
        }
    };

    React.useEffect(() => {
        fetchReviewData();
    }, [client, props.primaryAnalystSetId, props]);


    const renderAccepabilityOrVisualReviewDashboard = () => {


        if (props.loadingState === LoadingStateEnum.Loaded && !currentCoughAnalysisSetVisualReviewStatus) {
            return <AcceptabilityDashboard />;
        } else if (props.loadingState === LoadingStateEnum.Loaded && currentCoughAnalysisSetVisualReviewStatus) {
            return <VisualInspectionDashboard />;
        }
    };


    const keyMap = {
        zoomIn: "x",
        zoomOut: "z",
        resetToZero: "home",
        skipBackward: "left",
        skipForward: "right",
        toggleKeyboardShortcutDisplay: "alt",
        toggleGridline: "g",
        playPause: "space"
    };

    const handlers = {
        zoomIn: () =>
            props.dispatch(
                Actions.SetZoomLevelAction.create(props.zoomLevel - 1)
            ),
        zoomOut: () =>
            props.dispatch(
                Actions.SetZoomLevelAction.create(props.zoomLevel + 1)
            ),
        resetToZero: () => {
            props.dispatch(Actions.PauseAction.create());
            props.dispatch(Actions.ResetAction.create());
        },
        skipBackward: () =>
            props.dispatch(Actions.SkipBackwardAction.create()),
        skipForward: () =>
            props.dispatch(Actions.SkipForwardAction.create()),
        toggleKeyboardShortcutDisplay: () =>
            props.dispatch(
                Actions.ToggleKeyboardShortcutsAction.create()
            ),
        toggleGridline: () =>
            props.dispatch(Actions.ToggleGridlineAction.create()),
        playPause: event => {
            event.preventDefault();

            if (props.isPlaying) {
                props.dispatch(Actions.PauseAsyncAction.create());
            } else {
                props.dispatch(Actions.PlayAsyncAction.create());
            }
        }
    };

    if (props.playbackStatus !== null && props.playbackStatus !== undefined)
        return (<Alert show={true} category={AlertCategoryEnum.Danger}>{props.playbackStatus}</Alert>);

    if (props.isLoadError) {
        return <ServerError />;
    }

    return (
        <ErrorBoundary>
                <GlobalHotKeys keyMap={keyMap} handlers={handlers} allowChanges />
            <div>
                    {renderViewModeInfo()}
                    {renderLoadingBar()}
                    {renderErrorsBar()}
                    {renderLinkedWarning()}
                    <ResumePlayback />
                    <ItemMetadata
                        metadata={props.sessionMetadata}
                    />
                    {renderWaveformContainer()}
                    {renderAccepabilityOrVisualReviewDashboard()}
                    {renderAnalysisSetDialog()}
                    <CommentsComparisonDialog
                        show={props.isFixedCommentsDialogOpen}
                        isViewOnly={props.isViewOnly}
                        header="Analysis Set Comments"
                        primarySetId={props.primaryAnalystSetId}
                        sets={props.analysisSets}
                        comments={props.availableFixedComments}
                        onChange={(id, checked) =>
                            props.dispatch(
                                Actions.ToggleFixedCommentAction.create(
                                    id,
                                    checked,
                                    props.primaryAnalystSetId,

                                )
                            )
                        }
                        onClose={() =>
                            props.dispatch(
                                Actions.CloseFixedCommentsDialogAction.create()
                            )
                        }
                    />
                    {props.configErrorMessage === null && props.loadingState === LoadingStateEnum.Loaded ? <HourSegmentsModal /> : null}
                    {props.loadingState === LoadingStateEnum.Loaded ? <EventList /> : null}
                </div>
                <NotesModal
                    header="Session Notes"
                    isVisible={props.isSessionNotesVisible}
                    isAddingNote={props.isAddingNote}
                    notes={props.sessionNotes.map(sn => ({
                        id: sn.Id,
                        sessionId: sn.SessionId,
                        comment: sn.Text,
                        recorded: sn.CreatedDate,
                        userEmailAddress: sn.Username
                    }))}
                    onAdd={annotation =>
                        props.dispatch(
                            Actions.AddNewNoteAsyncAction.create(legacyClient, annotation)
                        )
                    }
                    onCloseModal={() =>
                        props.dispatch(
                            Actions.ToggleSessionNotesVisibilityAction.create()
                        )
                    }
                    disabled={false}
                    notificationBanner={<FilteredNotesNotificationBanner />}
                    />
            </ErrorBoundary>
    );
}

function mapStateToProps(state: AnalyseState): AnalyseAppProps {
    return {
        studyId: state.core.studyId,
        isViewOnly: state.core.isViewOnly,
        sessionMetadata: state.core.sessionMetadata,
        mode: state.core.mode,
        isEditMode: state.core.isEditMode,
        isPlaying: state.core.playState === PlayStateEnum.Playing,
        primaryAnalystSetId: state.analysisSet.primaryAnalystSetId,
        loadingState: state.core.loadingState,
        isLoadError: state.core.isLoadError,
        configErrorMessage: state.core.configErrorMessage,
        isFixedCommentsDialogOpen: state.core.isFixedCommentsDialogOpen,
        availableFixedComments: state.core.availableFixedComments,
        zoomLevel: state.core.zoomLevel,
        selectedWaveform: state.core.selectedWaveform,

        isSessionNotesVisible: state.core.isSessionNotesVisible,
        sessionNotes: state.core.sessionNotes,
        isAddingNote: state.core.isAddingSessionNote,

        analysisSetsLoaded: state.analysisSet.isLoaded,
        analysisSets: state.analysisSet.coughAnalysisSets,

        playbackStatus: state.core.playbackServiceStatus,
        positions: state.core.positions,
        comments: state.core.comments,
        coughAnalysisSet: state.core.coughAnalysisSet,
        blindComments: state.core.blindComments
        
    } as AnalyseAppProps;
}

export default connect<AnalyseAppProps, any, void>(mapStateToProps)(AnalyseApp);
